import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, Search } from 'lucide-react';
import DayView from './DayView';
import DayViewMobile from './DayViewMobile'; // Nouvelle importation pour mobile
import WeekView from './WeekView';
import MonthView from './MonthView';
import MonthViewMobile from './MonthViewMobile'; // Nouvelle importation pour mobile
import WeekViewMobile from './WeekViewMobile'; // Nouvelle importation

const Planning = ({ db, currentUser }) => {

  // Récupérer la date sauvegardée depuis localStorage ou utiliser aujourd'hui
  const getSavedDate = () => {
    const savedDate = localStorage.getItem('planningCurrentDate');
    if (savedDate) {
      const parsedDate = new Date(savedDate);
      // Vérifier que la date est valide
      return !isNaN(parsedDate.getTime()) ? parsedDate : new Date();
    }
    return new Date();
  };

  const [currentView, setCurrentView] = useState('day');
  const [currentDate, setCurrentDate] = useState(getSavedDate());
  const [adminRoleIds, setAdminRoleIds] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showCompletedAppointments, setShowCompletedAppointments] = useState(false);
  const [filterText, setFilterText] = useState(''); // État pour le filtre de recherche global


  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // Ne plus forcer le changement de vue sur mobile
    // La vue mois mobile est maintenant disponible
  }, [isMobile, currentView]);

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    switch (currentView) {
      case 'day':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() - 1);
        break;
      default:
        break;
    }
    setCurrentDate(newDate);
  };

  const goToNext = () => {
    const newDate = new Date(currentDate);
    switch (currentView) {
      case 'day':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + 7);
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + 1);
        break;
      default:
        break;
    }
    setCurrentDate(newDate);
  };

  const formatDateRange = () => {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };

    const getWeekNumber = (date) => {
      const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
      const dayNum = d.getUTCDay() || 7;
      d.setUTCDate(d.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
      return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    };

    switch (currentView) {
      case 'day':
        const weekNum = getWeekNumber(currentDate);
        return `${currentDate.toLocaleDateString('fr-FR', options)} | S${weekNum}`;
      case 'week':
        const weekStart = new Date(currentDate);
        const dayOfWeek = weekStart.getDay();
        const startOfWeek = new Date(weekStart);
        startOfWeek.setDate(weekStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        const weekEnd = new Date(startOfWeek);
        weekEnd.setDate(startOfWeek.getDate() + 6);
        const weekNumStart = getWeekNumber(startOfWeek);
        return `${startOfWeek.getDate()} - ${weekEnd.getDate()} ${weekEnd.toLocaleDateString('fr-FR', { month: 'short' })} ${weekEnd.getFullYear()} | S${weekNumStart}`;
      case 'month':
        return currentDate.toLocaleDateString('fr-FR', { year: 'numeric', month: 'long' });
      default:
        return '';
    }
  };

  // Sauvegarder la date dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('planningCurrentDate', currentDate.toISOString());
  }, [currentDate]);

  useEffect(() => {
    // Logique pour suivre les changements de vue
  }, [currentView]);

  return (
    <div className="pv-container">
      <div className="pv-controls">
        <div className="pv-view-buttons">
          <button
            onClick={() => setCurrentView('day')}
            className={currentView === 'day' ? 'pv-button pv-button-primary' : 'pv-button'}
          >
            Jour
          </button>
          <button
            onClick={() => setCurrentView('week')}
            className={currentView === 'week' ? 'pv-button pv-button-primary' : 'pv-button'}
          >
            Semaine
          </button>
          <button
            onClick={() => setCurrentView('month')}
            className={currentView === 'month' ? 'pv-button pv-button-primary' : 'pv-button'}
          >
            Mois
          </button>
          <button
            onClick={goToToday}
            className="pv-today-button pv-button"
          >
            <Calendar size={16} />
            <span>Aujourd'hui</span>
          </button>
        </div>
        <div className="pv-date-navigation">
          <>
            <button
              onClick={goToPrevious}
              className="pv-button-icon"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="pv-date-range">
              {formatDateRange()}
            </span>
            <button
              onClick={goToNext}
              className="pv-button-icon"
            >
              <ChevronRight size={18} />
            </button>
          </>
        </div>
        {/* Barre de recherche globale */}
        <div className="relative mx-4 flex-1 max-w-xs md:max-w-md hidden md:block">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={16} className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Rechercher (Client, Intervenant)..."
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition duration-150 ease-in-out"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        {/* Toggle optimisé pour voir les rendez-vous terminés */}
        <div className="pv-completed-toggle">
          <div className="pv-toggle-container">
            <input
              type="checkbox"
              id="showCompletedAppointments"
              checked={showCompletedAppointments}
              onChange={(e) => setShowCompletedAppointments(e.target.checked)}
              className="pv-toggle-checkbox"
            />
            <label htmlFor="showCompletedAppointments" className="pv-toggle-label">
              <span className="pv-toggle-track">
                <span className="pv-toggle-thumb"></span>
              </span>
              <span className="pv-toggle-text">
                {showCompletedAppointments ? 'Masquer terminés' : 'Voir terminés'}
              </span>
            </label>
          </div>
        </div>
      </div>
      <div className="pv-view-container" style={{ height: 'auto', overflowY: 'visible' }}>
        {currentView === 'day' && (
          isMobile ? (
            <DayViewMobile
              db={db}
              currentDate={currentDate}
              currentUser={currentUser}
              onDateChange={setCurrentDate}
              showCompletedAppointments={showCompletedAppointments}
            />
          ) : (
            <DayView
              db={db}
              currentDate={currentDate}
              currentUser={currentUser}
              showCompletedAppointments={showCompletedAppointments}
              filterText={filterText}
            />
          )
        )}
        {currentView === 'week' && (
          isMobile ? (
            <WeekViewMobile
              db={db}
              currentDate={currentDate}
              currentUser={currentUser}
              onDateChange={setCurrentDate}
              showCompletedAppointments={showCompletedAppointments}
            />
          ) : (
            <WeekView
              db={db}
              currentDate={currentDate}
              currentUser={currentUser}
              onDateChange={setCurrentDate}
              showCompletedAppointments={showCompletedAppointments}
              filterText={filterText}
            />
          )
        )}
        {currentView === 'month' && (
          isMobile ? (
            <MonthViewMobile
              db={db}
              currentDate={currentDate}
              currentUser={currentUser}
              onDateChange={setCurrentDate}
              showCompletedAppointments={showCompletedAppointments}
            />
          ) : (
            <MonthView
              db={db}
              currentDate={currentDate}
              currentUser={currentUser}
              showCompletedAppointments={showCompletedAppointments}
              filterText={filterText}
            />
          )
        )}
      </div>
    </div >
  );
};

export default Planning;
